import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, combineLatest, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { DashboardComponent } from '../dashboard/dashboard.component';

@Component({
  selector: 'app-dashboard-comparison',
  standalone: true,
  imports: [CommonModule, DashboardComponent],
  templateUrl: './dashboard-comparison.component.html',
  styleUrl: './dashboard-comparison.component.scss',
})
export class DashboardComparisonComponent implements OnInit, OnDestroy {
  leftConfig = '';
  rightConfig = '';
  renderDashboards = true;

  private sub = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
  ) {}

  ngOnInit(): void {
    const sideNavConfig$ = this.http
      .get<any[]>('assets/side-navbar-config/sidenav.config.json')
      .pipe(
        map((items) => this.findCompareConfig(items)),
        catchError((err) => {
          console.error('Error loading comparison config from side nav:', err);
          return of(null);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.sub.add(
      combineLatest([this.route.queryParamMap, sideNavConfig$]).subscribe(([params, sideNavCompare]) => {
        const routeParams = this.route.snapshot.paramMap;
        this.leftConfig = this.normalizeConfigName(
          routeParams.get('left') ||
            params.get('left') ||
            params.get('a') ||
            sideNavCompare?.left ||
            '',
        );
        this.rightConfig = this.normalizeConfigName(
          routeParams.get('right') ||
            params.get('right') ||
            params.get('b') ||
            sideNavCompare?.right ||
            '',
        );
        this.renderDashboards = false;
        queueMicrotask(() => {
          this.renderDashboards = true;
        });
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private normalizeConfigName(value: string): string {
    const trimmed = String(value || '').trim();
    if (!trimmed) {
      return '';
    }

    const withoutPath = trimmed.split(/[\\/]/).pop() || trimmed;
    return withoutPath.toLowerCase().endsWith('.json') ? withoutPath : `${withoutPath}.json`;
  }

  private findCompareConfig(items: any[] | null | undefined): { left?: string; right?: string } | null {
    const flatItems = this.flattenMenuItems(items || []);
    const currentPath = `/${this.route.snapshot.routeConfig?.path || 'compare'}`.replace(/\/:.+$/g, '');
    const match =
      flatItems.find((item) => this.normalizePath(item?.path) === '/compare') ||
      flatItems.find((item) => this.normalizePath(item?.path) === currentPath) ||
      flatItems.find((item) => item?.compare?.left || item?.compare?.right);

    return match?.compare || null;
  }

  private flattenMenuItems(items: any[]): any[] {
    return items.flatMap((item) => [
      item,
      ...this.flattenMenuItems(Array.isArray(item?.children) ? item.children : []),
    ]);
  }

  private normalizePath(path: string): string {
    const rawPath = String(path || '').trim();
    const pathOnly = rawPath.split('?')[0];
    return pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
  }
}
